// Runs the doctor's EMR availability search for the single visit being booked.

import { useCallback, useEffect, useMemo, useState } from 'react'

import { toSiteWallClockDate } from 'common/services/dateService'
import { useLazyFindAvailableSlotsEmrQuery } from 'features/Schedule/api/schedule.api'
import type { EmrAvailableSlots } from 'features/Schedule/api/schedule.types'

import type { DayResults, SearchCriteria, SlotOption } from './ehrAppointmentV2.types'
import { groupByDay } from './pairing'

type SearchArgs = {
  siteId?: string
  duration: number
  categoryCode?: number
  emrAppointmentId?: number | null
  criteria: SearchCriteria
  timeZone?: string
}

// What the payload says, read as the site reads it. The viewer's zone never takes part.
type SlotInstant = {
  day: string
  minutes: number
}

const hasZone = (value: string): boolean => /[zZ]$/.test(value) || /[+-]\d{2}:?\d{2}$/.test(value)

const isTwelveHour = (value: string): boolean => /\b[ap]\.?m\.?/i.test(value)

const pad = (value: number): string => String(value).padStart(2, '0')

// A zoned instant belongs to whatever day and clock the site is on at that moment.
const fromZoned = (value: string, timeZone?: string): SlotInstant | null => {
  const date = toSiteWallClockDate(value, timeZone)
  if (!date || Number.isNaN(date.getTime())) return null

  return {
    day: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    minutes: date.getHours() * 60 + date.getMinutes(),
  }
}

// A naive string is already the site's wall clock, so its digits are taken as written.
const fromNaive = (value: string): SlotInstant | null => {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/)
  if (!match) return null

  const hours = Number(match[2])
  const minutes = Number(match[3])
  if (hours > 23 || minutes > 59) return null

  return { day: match[1], minutes: hours * 60 + minutes }
}

export const readSlotInstant = (value: unknown, timeZone?: string): SlotInstant | null => {
  if (typeof value !== 'string' || !value) return null
  // A twelve hour clock cannot be told from a broken one, so it is never guessed at.
  if (isTwelveHour(value)) return null

  return hasZone(value) ? fromZoned(value, timeZone) : fromNaive(value)
}

type EmrDay = {
  date?: unknown
  times?: unknown
}

// OpenEMR answers days: [{ date, times: ['08:00', ...] }]; each time starts a visit of the searched length.
export const toEmrSlotOptions = (
  payload: EmrAvailableSlots | undefined,
  duration: number,
  timeZone?: string,
): SlotOption[] => {
  if (!payload || !Array.isArray(payload.days)) return []

  return payload.days.flatMap((day: EmrDay) => {
    if (typeof day?.date !== 'string' || !Array.isArray(day.times)) return []

    return day.times.reduce<SlotOption[]>((found, time) => {
      const clock = typeof time === 'string' ? time.match(/^(\d{1,2}):(\d{2})$/) : null
      if (!clock) return found

      const start = readSlotInstant(
        `${day.date} ${clock[1].padStart(2, '0')}:${clock[2]}`,
        timeZone,
      )
      if (!start || start.day !== day.date) return found

      return [...found, { day: start.day, start: start.minutes, end: start.minutes + duration }]
    }, [])
  })
}

const RERUN_DELAY_MS = 350

export const useAvailabilitySearch = ({
  siteId,
  duration,
  categoryCode,
  emrAppointmentId,
  criteria,
  timeZone,
}: SearchArgs) => {
  const [findAvailableSlots, { data, isFetching, error }] = useLazyFindAvailableSlotsEmrQuery()
  const [hasSearched, setHasSearched] = useState(false)
  const [shownFor, setShownFor] = useState<string | null>(null)
  // Bumped on every run so data read alongside the search is fetched fresh too.
  const [searchId, setSearchId] = useState(0)

  const canSearch = Boolean(siteId && duration > 0)

  // Results only belong to the query that asked for them.
  const queryKey = `${duration}-${categoryCode ?? 0}-${emrAppointmentId ?? 0}-${
    criteria.windowStart
  }-${criteria.windowDays}`

  const run = useCallback(async () => {
    if (!canSearch) return
    setHasSearched(true)
    setShownFor(queryKey)
    setSearchId((current) => current + 1)
    await findAvailableSlots({
      siteId,
      startdate: criteria.windowStart,
      searchdays: criteria.windowDays,
      evdur: duration,
      catid: categoryCode || undefined,
      eid: emrAppointmentId || undefined,
    })
  }, [
    canSearch,
    categoryCode,
    criteria.windowStart,
    criteria.windowDays,
    duration,
    emrAppointmentId,
    findAvailableSlots,
    queryKey,
    siteId,
  ])

  const clear = useCallback(() => {
    setHasSearched(false)
    setShownFor(null)
  }, [])

  const isCurrent = hasSearched && shownFor === queryKey
  // Inputs changed under an open list: it stays open and reads as loading until the rerun lands.
  const isStale = hasSearched && !isCurrent && canSearch

  // Rerun once edits settle, so typing a duration does not fire a search per keystroke.
  useEffect(() => {
    if (!isStale) return
    const timer = setTimeout(() => void run(), RERUN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isStale, run])

  const results = useMemo<DayResults[]>(() => {
    if (!isCurrent) return []
    return groupByDay(toEmrSlotOptions(data, duration, timeZone))
  }, [data, duration, isCurrent, timeZone])

  // OpenEMR can answer 200 with success false; that is a failure, not an empty week.
  const hasError = isCurrent && (Boolean(error) || (!isFetching && data?.success === false))

  // A payload that parsed to nothing must not read as "fully booked".
  const isUnreadable =
    isCurrent &&
    !isFetching &&
    !hasError &&
    data !== undefined &&
    (!Array.isArray(data.days) || (data.days.length > 0 && results.length === 0))

  return {
    run,
    clear,
    results,
    searchId,
    hasSearched,
    isFetching: isFetching || isStale,
    hasError,
    isUnreadable,
    canSearch,
  }
}
